import { inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { Consent, CookieCategories } from '../../models/usercentrics-cookiebot';
import { INITIAL_COOKIE_CONSENT_STATE } from '../../constants/usercentrics-cookiebot';
import { parseJSON } from '../../utils/json';
import { PlatformDataService } from '../../services/platform-data';

/**
 * Service for managing the application's cookie consent state using NgRx Signals.
 * It integrates with ngx-cookie-service-ssr for server-side rendering (SSR)
 * and with the Cookiebot (or Usercentrics) global object for client-side updates.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentStore {
  private readonly platformDataService = inject(PlatformDataService);
  private readonly ssrCookieService = inject(SsrCookieService);

  readonly cookieConsentState = signalState(this.getNewState(parseJSON<Consent>(this.ssrCookieService.get('CookieConsent'))));

  readonly marketingConsent = this.cookieConsentState.marketing;
  readonly statisticsConsent = this.cookieConsentState.statistics;
  readonly preferencesConsent = this.cookieConsentState.preferences;

  constructor() {
    if (this.platformDataService.isBrowser) {
      this.attachEventListeners();
    }
  }

  /**
   * Attaches event listeners to the window object to handle Cookiebot consent changes.
   *
   * This private method sets up callbacks that are triggered when a user
   * interacts with the Cookiebot consent banner (either by accepting or declining cookies).
   * Upon interaction, it updates the application's internal cookie consent state
   * and reloads the page to ensure all scripts and functionalities adhere to the
   * new consent preferences.
   *
   * @private
   */
  private attachEventListeners(): void {
    const thisWindow = this.platformDataService.window;

    if (thisWindow) {
      // Define the callback function to update consent from the window object
      const updateFromWindow = (): void => {
        // Access Cookiebot.consent again to get the latest state after user interaction
        patchState(this.cookieConsentState, this.getNewState(thisWindow.Cookiebot?.consent));
      };

      // Directly assign to the global callback properties.
      // These are globally declared and expected to exist if Cookiebot script is loaded.
      thisWindow.CookiebotCallback_OnAccept = updateFromWindow;
      thisWindow.CookiebotCallback_OnDecline = updateFromWindow;
    }
  }

  /**
   * Computes a new cookie consent state.
   * Falls back to initial values for any undefined consent categories.
   *
   * @param cookieConsentObj The new consent object to apply, or `undefined` to use default values.
   * @returns A `CookieCategories` object representing the updated consent state.
   */
  private getNewState(cookieConsentObj?: Consent): CookieCategories {
    return {
      marketing: cookieConsentObj?.marketing ?? INITIAL_COOKIE_CONSENT_STATE.marketing,
      statistics: cookieConsentObj?.statistics ?? INITIAL_COOKIE_CONSENT_STATE.statistics,
      preferences: cookieConsentObj?.preferences ?? INITIAL_COOKIE_CONSENT_STATE.preferences
    };
  }
}
